import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/layout/PageHeader';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { DataTableSkeleton } from '@/components/ui/data-table-skeleton';
import { ScrapingQueueStats } from '@/components/jobs/ScrapingQueueStats';
import { ScrapingQueueTable } from '@/components/jobs/ScrapingQueueTable';
import { useScrapingQueue, useRetryScrapingJob, useDeleteQueueItem } from '@/hooks/useJobs';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { QueueFilters, ScrapingQueueItem, ScrapingStatus } from '@repo/types';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/jobs/scraping-queue')({
  component: ScrapingQueuePage,
});

// Persisted, off by default: remembers the user's auto-refresh preference.
const autoRefreshAtom = atomWithStorage('oubo-scraping-auto-refresh', false);

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'retrying', label: 'Retrying' },
];

function ScrapingQueuePage() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [jobKeyFilter, setJobKeyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [live, setLive] = useAtom(autoRefreshAtom);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 300);

  const filters: QueueFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      jobKey: jobKeyFilter ? parseInt(jobKeyFilter, 10) : undefined,
      status: statusFilter !== 'all' ? [statusFilter as ScrapingStatus] : undefined,
    }),
    [debouncedSearch, jobKeyFilter, statusFilter]
  );

  const { data, isLoading, isFetching, error, refetch } = useScrapingQueue(
    filters,
    { page, limit: pageSize },
    {
      placeholderData: keepPreviousData,
      // Poll every 30s while live — but tighten to 10s when work is in flight.
      // Polling stops when this page unmounts (no observers) and pauses while the
      // tab is backgrounded, so it never leaks requests in the background.
      refetchInterval: live
        ? (query) => {
            const busy = query.state.data?.queueItems?.some(
              (item: ScrapingQueueItem) => item.status === 'processing' || item.status === 'retrying'
            );
            return busy ? 10000 : 30000;
          }
        : false,
      refetchIntervalInBackground: false,
    }
  );

  const retryMutation = useRetryScrapingJob();
  const deleteMutation = useDeleteQueueItem();

  const handleRetry = (id: string) => retryMutation.mutate(id);
  const handleDelete = (id: string) => deleteMutation.mutate(id);
  const handleView = (item: ScrapingQueueItem) => {
    if (item.jobId) navigate({ to: '/admin/jobs/$id/details', params: { id: item.jobId } });
  };
  const handleStart = () =>
    toast.info("Scraping runs automatically — manual start isn't available yet.");

  // Guarantee a visible spin even when the refetch resolves instantly (cached).
  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch();
    window.setTimeout(() => setIsRefreshing(false), 600);
  };
  const refreshing = isRefreshing || isFetching;

  const total = data?.pagination?.total ?? 0;
  const totalPages = total ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const rangeStart = total ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(page * pageSize, total);

  const resetPage = () => setPage(1);
  const hasActiveFilters = Boolean(debouncedSearch || jobKeyFilter || statusFilter !== 'all');

  const chips: { key: string; label: string; onRemove: () => void }[] = [];
  if (debouncedSearch)
    chips.push({ key: 'search', label: `Search: ${debouncedSearch}`, onRemove: () => setSearch('') });
  if (jobKeyFilter)
    chips.push({ key: 'jobKey', label: `Job ID: ${jobKeyFilter}`, onRemove: () => setJobKeyFilter('') });
  if (statusFilter !== 'all')
    chips.push({
      key: 'status',
      label: `Status: ${statusFilter}`,
      onRemove: () => setStatusFilter('all'),
    });

  const clearFilters = () => {
    setSearch('');
    setJobKeyFilter('');
    setStatusFilter('all');
    resetPage();
  };

  return (
    <div className="space-y-6 p-4 duration-500 ease-out animate-in fade-in slide-in-from-bottom-6 sm:p-6">
      <PageHeader
        title="Scraping Queue"
        description="Monitor and manage AI-powered job scraping operations."
        actions={
          <>
            <Label
              htmlFor="live"
              className="flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground"
            >
              <span
                className={cn(
                  'size-2 rounded-full',
                  live ? 'animate-pulse bg-emerald-500' : 'bg-muted-foreground/40'
                )}
              />
              Auto-refresh
            </Label>
            <Switch id="live" checked={live} onCheckedChange={setLive} />
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={cn('size-4', refreshing && 'animate-spin')} />
              Refresh
            </Button>
          </>
        }
      />

      {/* Stats */}
      {data?.stats && <ScrapingQueueStats stats={data.stats} />}

      {/* Filters */}
      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by URL, title, employee…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetPage();
              }}
              className="pl-9"
            />
          </div>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <Input
              type="number"
              placeholder="Job ID"
              value={jobKeyFilter}
              onChange={(e) => {
                setJobKeyFilter(e.target.value);
                resetPage();
              }}
              className="min-w-0 flex-1 sm:w-[120px] sm:flex-none"
            />
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                resetPage();
              }}
            >
              <SelectTrigger className="min-w-0 flex-1 sm:w-[160px] sm:flex-none">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="size-4" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {chips.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1 rounded-full border bg-muted/50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-muted-foreground duration-200 animate-in fade-in zoom-in-95"
              >
                {chip.label}
                <button
                  type="button"
                  onClick={chip.onRemove}
                  aria-label={`Remove ${chip.label}`}
                  className="flex size-4 items-center justify-center rounded-full transition-colors hover:bg-foreground/10 hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load the scraping queue.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Table */}
      {isLoading ? (
        <DataTableSkeleton columns={5} />
      ) : (
        <>
          <ScrapingQueueTable
            items={data?.queueItems ?? []}
            onRetry={handleRetry}
            onDelete={handleDelete}
            onView={handleView}
            onStart={handleStart}
          />

          {total > 0 && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Showing <span className="font-medium text-foreground">{rangeStart}</span>–
                <span className="font-medium text-foreground">{rangeEnd}</span> of{' '}
                <span className="font-medium text-foreground">{total}</span> items
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rows</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(value) => {
                      setPageSize(Number(value));
                      resetPage();
                    }}
                  >
                    <SelectTrigger className="h-8 w-[72px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 20, 50].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
